/**
 * Getting hold of the peer connection, whichever direction the call came from.
 *
 * A JsSIP session does not always have an `RTCPeerConnection` on it. `_connection` starts
 * as null and is built inside `connect()` for an outgoing call and inside `answer()` for
 * an incoming one — so a call that is merely *arriving* has nothing to attach media to
 * yet, and reading `.addEventListener` off it throws.
 *
 * That is not a cosmetic failure, which is why this lives in its own file with a test
 * rather than inline. JsSIP emits `newRTCSession` and then sends `180 Ringing` in a single
 * comma sequence with nothing catching in between:
 *
 *     this._newRTCSession("remote", i),
 *     this._status !== R.STATUS_TERMINATED && (i.reply(180, ...), this._progress(...))
 *
 * So a throw in the handler takes the ringing with it. The PBX sends its INVITE, gets a
 * `100 Trying` from the transaction layer and then nothing — no `180` — and gives up. In
 * the Asterisk log that is `Called PJSIP/7001` with no `is ringing` after it, which reads
 * as an endpoint that is not registered. It is a console with a live telephone and a
 * broken handler. Worse, the throw also skips whatever the caller meant to do next, so
 * the ringing state is never set and no Answer button is ever drawn.
 *
 * Outgoing calls hide all of this, because `ua.call()` builds the connection before any
 * of it runs. The same bug lived in `docker/asterisk/static/webphone.html` and was
 * invisible for exactly as long, for exactly that reason.
 */

/**
 * What this needs of a JsSIP session, which is nearly nothing.
 *
 * Generic in the connection rather than naming `RTCPeerConnection`, so that the decision
 * can be tested where there is no DOM. `tests/js` compiles without DOM types on purpose —
 * it is the suite for logic that does not need a browser, and this logic does not.
 */
export interface PeerConnectionSource<TConnection> {
  /** Null until JsSIP builds one — see above. */
  connection: TConnection | null;
  on(
    event: 'peerconnection',
    listener: (event: { peerconnection: TConnection }) => void,
  ): void;
}

/**
 * Hand `bind` the peer connection, now if there is one and later if there is not.
 *
 * Later means JsSIP's `peerconnection` event, which it emits from `_createRTCConnection`
 * the moment it makes one — so an incoming call binds when it is answered, and an
 * outgoing call binds immediately.
 */
export function whenPeerConnection<TConnection>(
  session: PeerConnectionSource<TConnection>,
  bind: (connection: TConnection) => void,
): void {
  if (session.connection !== null && session.connection !== undefined) {
    bind(session.connection);
    return;
  }

  session.on('peerconnection', (event) => bind(event.peerconnection));
}
